import ModalWrapper from '@Components/Modals/ModalWrapper/ModalWrapper';
import TaxStatusServices from '@Services/TaxStatus.services.ts';
import { TaxStatus } from '@Types/Asset.types';
import { NewTaxStatusFields } from '@Types/Form.types';
import { formatRequired } from '@Utils/form.utils';
import { handleRequestError } from '@Utils/request.utils';
import { VALIDATION_RULES } from '@Utils/rule.utils.ts';
import { ErrorDisplay, FreeText, Toast } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

type Props = {
  show: boolean;
  taxStatus?: TaxStatus;
  onClose: () => void;
  onSuccess: () => void;
};

const { TAX_STATUS } = VALIDATION_RULES;

const TaxStatusModal = ({ show, taxStatus, onClose, onSuccess }: Props) => {
  const {
    handleSubmit,
    formState: { errors },
    reset,
    register,
    setValue,
  } = useForm<NewTaxStatusFields>();

  useEffect(() => {
    reset();
    if (taxStatus) {
      setValue('description', taxStatus.description);
    }
  }, [taxStatus, show]);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const onSubmitSuccess = () => {
    Toast.success(taxStatus ? 'Tax status updated successfully.' : 'Tax status created successfully.');
    onSuccess();
    reset();
    onClose();
  };

  const createTaxStatus = (fields: NewTaxStatusFields) => {
    TaxStatusServices.createTaxStatus(fields)
      .then(() => onSubmitSuccess())
      .catch((errorResponse) => handleRequestError(errorResponse, setErrorMessage, true));
  };

  const updateTaxStatus = (fields: NewTaxStatusFields) => {
    TaxStatusServices.updateTaxStatus(taxStatus?.id, fields)
      .then(() => onSubmitSuccess())
      .catch((errorResponse) => handleRequestError(errorResponse, setErrorMessage, true));
  };

  const title = taxStatus ? 'Edit Tax Status' : 'Add Tax Status';
  const handleTaxStatusSubmit = taxStatus ? updateTaxStatus : createTaxStatus;

  return (
    <div className='modal--fit-content modal--w-30vw modal--pt-25vh'>
      <ModalWrapper
        title={title}
        show={show}
        handleClose={onClose}
        primaryButtonText='Confirm'
        primaryAction={() => handleSubmit(handleTaxStatusSubmit)()}>
        {errorMessage ? (
          <ErrorDisplay inline details={errorMessage} />
        ) : (
          <form onSubmit={() => handleSubmit(handleTaxStatusSubmit)()}>
            <div className='grid'>
              <div className='col-12'>
                <FreeText
                  label={formatRequired('Description')}
                  fullWidth
                  error={!!errors.description}
                  errorMessage={errors.description?.message}
                  {...register('description', TAX_STATUS.DESCRIPTION)}
                />
                <br />
              </div>
            </div>
          </form>
        )}
      </ModalWrapper>
    </div>
  );
};

export default TaxStatusModal;
